import { defineStore } from 'pinia'
import { ref } from 'vue'

export type ThemeChoice = 'system' | 'light' | 'dark'

const STORAGE_KEY = 'loyalty-portal-theme'

/**
 * Light, dark, or whatever the operating system says. A per-browser
 * convenience, so localStorage is the right home for it.
 */
export const useThemeStore = defineStore('theme', () => {
  const choice = ref<ThemeChoice>(read())

  function read(): ThemeChoice {
    try {
      const stored = localStorage.getItem(STORAGE_KEY)
      return stored === 'light' || stored === 'dark' ? stored : 'system'
    } catch {
      // Private browsing, or site data blocked.
      return 'system'
    }
  }

  function apply(): void {
    const root = document.documentElement
    if (choice.value === 'system') {
      root.removeAttribute('data-theme')
    } else {
      root.setAttribute('data-theme', choice.value)
    }
  }

  function set(next: ThemeChoice): void {
    choice.value = next
    apply()
    try {
      localStorage.setItem(STORAGE_KEY, next)
    } catch {
      // Not being able to remember the choice is not worth surfacing.
    }
  }

  function cycle(): void {
    set(choice.value === 'system' ? 'light' : choice.value === 'light' ? 'dark' : 'system')
  }

  return { choice, apply, set, cycle }
})
